import * as React from 'react';
import {Link as RoutedLink} from 'react-router-dom';

import {Button} from '../button/button';
import {EmptyStateLarge} from '../empty-states/empty-state-large';
import {Link} from '../link/link';

type Props = {
    status: 'QUEUED' | 'NEEDS_ATTENTION' | 'READY_TO_SEND';
    hasAutomatedEmailSequences: boolean;
    hasConfiguredAutomations: boolean;
};

export function EmptyState(props: Props) {
    const {status} = props;

    if (status === 'QUEUED' && props.hasConfiguredAutomations) {
        return (
            <EmptyStateLarge
                title='Nothing to see here'
                subtitle={
                    <span>
                        <p>
                            You don’t have any new automated emails sending to your leads right now.
                            As soon as a lead enters a pipeline stage that has an automated email
                            set up to send from you, you’ll be able to check out that email here.
                        </p>
                        <p>
                            You can find your already {/* @ts-expect-error */}
                            <Link variant='primary' size='big' as={RoutedLink} to='/email/sent'>
                                sent
                            </Link>{' '}
                            and{' '}
                            <Link
                                variant='primary'
                                size='big'
                                // @ts-expect-error
                                as={RoutedLink}
                                to='/email/scheduled'
                            >
                                scheduled
                            </Link>{' '}
                            emails in the messages pages.
                        </p>
                    </span>
                }
                icon={
                    <img
                        alt='Configure email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_automations_sending_empty_state.png'
                    />
                }
                cta={
                    <Button
                        onClick={() => {
                            window.open(
                                'https://www.nutshell.com/tour/nutshell-crm/email/',
                                '_blank'
                            );
                        }}
                        size='xlarge'
                        variant='primary'
                    >
                        Learn more about email automation
                    </Button>
                }
            />
        );
    }

    if (status === 'QUEUED' && props.hasAutomatedEmailSequences) {
        return (
            <EmptyStateLarge
                title='Automate your email outreach'
                subtitle='Use Nutshell’s sales automation to automatically email leads as they move along your sales process'
                icon={
                    <img
                        alt='Configure email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_automations_sending_empty_state.png'
                    />
                }
                cta={
                    <Button
                        onClick={() => {
                            window.open(
                                'https://www.nutshell.com/tour/nutshell-crm/email/',
                                '_blank'
                            );
                        }}
                        size='xlarge'
                        variant='primary'
                    >
                        Learn more about email automation
                    </Button>
                }
            />
        );
    }

    if (status === 'QUEUED' && !props.hasAutomatedEmailSequences) {
        return (
            <EmptyStateLarge
                title='Automate your email outreach'
                subtitle='Use Nutshell’s sales automation to automatically email leads as they move along your sales process'
                icon={
                    <img
                        alt='Configure email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_automations_sending_pro_upsell.png'
                    />
                }
                cta={
                    <Link
                        variant='button-primary'
                        size='xlarge'
                        // @ts-expect-error
                        as={RoutedLink}
                        to='/setup/billing/sales'
                    >
                        Upgrade to Nutshell Pro
                    </Link>
                }
                dismissal={{
                    title: 'Learn more about email automation',
                    onClick: () => {
                        window.open('https://support.nutshell.com', '_blank');
                    },
                }}
            />
        );
    }

    if (status === 'NEEDS_ATTENTION') {
        return (
            <EmptyStateLarge
                title='Nothing but clear skies'
                subtitle='All of your automated emails are sending without any issues. If we do run into any emails that need your attention before we can send them, they’ll be shown here.'
                icon={
                    <img
                        alt='You’re all set graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_failed_empty_state.png'
                    />
                }
            />
        );
    }

    if (status === 'READY_TO_SEND') {
        return (
            <EmptyStateLarge
                title='Nothing to see here'
                subtitle='If any of your automated emails needed attention before they could be sent, the ones that you’ve already updated will appear here. Once you see emails here, go ahead and kick them off!'
                icon={
                    <img
                        alt='No email automations graphic'
                        style={{width: 300}}
                        src='/include/images/email-marketing/email_sent_empty_state.png'
                    />
                }
            />
        );
    }

    return null;
}
